import { h } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';

import { FeaturesJrRakuPack } from '@travel/icons/features';
import { Shinkansen } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import { getDefaultURLSearchRequest, URL_KEYS } from '@travel/traveler-core/utils/jrdpHelper';
import { IconText } from '@travel/ui';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';
import isNotEmptyArray from '@travel/ui/utils/arrays';

import EmptyState from 'components/EmptyState';
import PaginationButton from 'components/PaginationButton';
import PlanSearchForm from 'components/PlanSearchForm';
import PriceSwitchHeader from 'components/PriceSwitchHeader';
import TextBadge from 'components/TextBadge';
import isLazyLoadContext from 'context/isLazyLoadContext';
import useSearchForm from 'hooks/useSearchForm';
import { fetchMorePlans, fetchPlanListFilter, updatePendingFilters } from 'store/planList/actions';
import {
  getIsFetching,
  getIsFetchingFilters,
  getIsFetchingMorePlans,
  getPrefetchedFilters,
  hasMorePlans,
} from 'store/planList/selectors';
import PlanItem from '../PlanItem';
import PlanSkeleton from '../PlanSkeleton';

import { pushLocation } from 'store/__router/actions';

import { FetchPriority } from 'constants/common';
import paths from 'constants/paths';
import { PLAN_LIST_SORT } from 'constants/sort';
import {
  getDefaultURLCustomListRequest,
  getPlanListValidSortValue,
  getValidAccommodationsPath,
  toSearchQueryString,
} from 'utils/search';

import styles from './planTabSelection.module.scss';

import type { URLSearchQuery } from 'Common-Types';
import type { PlanList, RequestBody, SortCategory } from 'PlanList-Types';
import type { Props as PlanListRouteProps } from '../../index';

type Props = {
  planList: PlanList;
  /** Object of current active search */
  query?: URLSearchQuery;
  departureStation?: string;
  destinationStation?: string;
};

function PlanTabSelection(props: PlanListRouteProps & { className?: string } & Props) {
  const { planList, departureStation, destinationStation, query, params } = props;

  const planListUrl = paths.plansList.pathResolver(params.pageIdentifier);
  const pageNumber = params.pageNumber;

  const prefetchedFilters = useSelector(getPrefetchedFilters);
  const isFetchingFilters = useSelector(getIsFetchingFilters);
  const isFetchingMorePlans = useSelector(getIsFetchingMorePlans);
  const isFetching = useSelector(getIsFetching);
  const hasShowMore = useSelector(hasMorePlans);
  const { isPC } = useDeviceTypes();
  const dispatch = useDispatch();
  const accommodationAreaPath = planList.accommodationArea?.pathId;
  const accommodationsPath = getValidAccommodationsPath(planList.accommodations);

  const form = useSearchForm(query, { shouldFetchByDepartureArea: false });
  const isFormInitialized = useRef(false);

  const { onDepartureChanged, onSetAccommodationArea } = form.travel;
  useEffect(() => {
    if (!isFormInitialized.current && !isFetching && planList.departureArea?.code) {
      if (planList.departureArea) onDepartureChanged(planList.departureArea);
      if (planList.accommodationArea) onSetAccommodationArea(planList.accommodationArea);

      isFormInitialized.current = true;
    }
  }, [planList, onDepartureChanged, onSetAccommodationArea, isFetching]);

  const onSortChange = useCallback(
    (sort: SortCategory) => {
      dispatch(
        pushLocation({
          pathname: paths.plansList.pathResolver(params.pageIdentifier),
          search: toSearchQueryString({
            ...query,
            [URL_KEYS.SORT]: sort,
          }),
        }),
      );
    },
    [dispatch, params.pageIdentifier, query],
  );

  const onFilterSubmit = useCallback(
    (featureIds: Array<string>, minPrice?: number, maxPrice?: number) => {
      const updatedQueryString = toSearchQueryString({
        ...query,
        ...getDefaultURLCustomListRequest<SortCategory>({
          ...query,
          [URL_KEYS.MIN_PRICE]: minPrice,
          [URL_KEYS.MAX_PRICE]: maxPrice,
          [URL_KEYS.FEATURES]: featureIds,
        }),
      });

      dispatch(
        pushLocation({
          pathname: paths.plansList.pathResolver(params.pageIdentifier),
          search: updatedQueryString,
        }),
      );
    },
    [dispatch, params.pageIdentifier, query],
  );

  const onFilterRefetch = useCallback(
    (featureIds: Array<string>, minPrice?: number, maxPrice?: number) => {
      const searchQuery = {
        ...getDefaultURLSearchRequest({
          ...query,
          [URL_KEYS.ACCOMMODATION_AREA]: accommodationAreaPath,
          [URL_KEYS.PLACE_OF_STAY]: accommodationsPath,
        }),
        ...getDefaultURLCustomListRequest<SortCategory>({
          ...query,
          [URL_KEYS.FEATURES]: featureIds,
          [URL_KEYS.MIN_PRICE]: minPrice,
          [URL_KEYS.MAX_PRICE]: maxPrice,
        }),
      };

      dispatch(fetchPlanListFilter({ ...searchQuery, id: params.pageIdentifier }));
    },
    [dispatch, query, accommodationsPath, accommodationAreaPath, params.pageIdentifier],
  );

  const onResetFiltersPendingState = useCallback(() => {
    dispatch(updatePendingFilters(planList));
  }, [dispatch, planList]);

  const plans = planList.plans || [];

  const handlePagination = useCallback(() => {
    const searchQuery: RequestBody = {
      id: params.pageIdentifier,
      ...getDefaultURLSearchRequest({
        ...query,
        accommodationArea: planList.accommodationArea.pathId,
        placeOfStay: planList.accommodations.map(accommodation => accommodation.pathId).join(','),
      }),
      ...getDefaultURLCustomListRequest(query),
    } as RequestBody;

    dispatch(fetchMorePlans(searchQuery));
  }, [dispatch, query, planList, params.pageIdentifier]);

  return (
    <>
      <div className={styles.titleHeader}>
        <div className={styles.flex}>
          <IconText
            icon={<Shinkansen size={28} />}
            text={<Translate className={styles.title} id="Plan_List.List.Title" />}
            spaceBetween={8}
            className={styles.planTitleIcon}
            textClassName={styles.title}
          />
          {isPC && <h3 className={styles.subText}>* {form.labels.searchConditionMessage}</h3>}
        </div>
        <FeaturesJrRakuPack className={styles.jrRakuPack} />
      </div>

      <PlanSearchForm<PlanList, SortCategory>
        query={query}
        form={form}
        formAction={paths.plansList.pathResolver(params.pageIdentifier)}
        sort={getPlanListValidSortValue(query?.[URL_KEYS.SORT])}
        sortOptions={PLAN_LIST_SORT}
        prefetchedPlans={prefetchedFilters}
        plans={planList}
        isFetchingPlans={isFetching}
        isFetchingFilters={isFetchingFilters}
        onFilterSubmit={onFilterSubmit}
        onFilterRefetch={onFilterRefetch}
        onSortChange={onSortChange}
        onResetPendingState={onResetFiltersPendingState}
        onDepartureChanged={form.travel.onDepartureChanged}
      />

      {isFetching ? (
        <div class={styles.skeletonContainer} data-testid="PlanList-skeleton">
          <PlanSkeleton isPc={isPC} isFirst={isPC} />
          <PlanSkeleton isPc={isPC} />
          {!isPC && <PlanSkeleton isPc={isPC} />}
          <div class={styles.loading}>
            <LoadingCircle classType="primary" isLoading />
          </div>
        </div>
      ) : isNotEmptyArray(plans) ? (
        <div data-testid="plansList-container" className={styles.planList}>
          <PriceSwitchHeader
            totalCount={planList.total}
            isJrRakuPack={false}
            className={styles.priceSwitch}
          />
          <div className={styles.stationInfo}>
            <TextBadge
              text={<Translate id="DP_Common.Train.Route.Title" />}
              className={styles.priceTitle}
            />
            {departureStation && destinationStation && (
              <Translate
                id="DP_Common.Train.Route.Stations"
                data={{ departure_station: departureStation, arrival_station: destinationStation }}
                className={styles.stationNames}
              />
            )}
          </div>
          {plans.map((plan, index) => (
            <isLazyLoadContext.Provider
              key={plan.planId}
              value={{
                isDisableLazyLoad: index === 0 ? true : false,
                fetchPriority: index === 0 ? FetchPriority.HIGH : FetchPriority.LOW,
              }}
            >
              <PlanItem
                key={plan.planId}
                plan={plan}
                hotelId={props.params.pageIdentifier}
                query={props.query}
                accommodationAreaPath={accommodationAreaPath}
                accommodationsPath={accommodationsPath}
                departureAreaCode={planList.departureArea?.code}
                departureStation={departureStation}
                destinationStation={destinationStation}
              />
            </isLazyLoadContext.Provider>
          ))}
          {isFetchingMorePlans ? (
            <div class={styles.loading} data-testid="PlanList-fetchMoreLoading">
              <LoadingCircle classType="primary" isLoading />
            </div>
          ) : (
            hasShowMore && (
              <PaginationButton
                className={styles.seeMoreButton}
                limit={planList.limit}
                offset={planList.offset}
                total={planList.total}
                onClick={handlePagination}
                to={planListUrl}
                pageNumber={pageNumber}
              />
            )
          )}
        </div>
      ) : (
        <EmptyState
          title={<Translate id="Plan_List.Message.No_Result" />}
          description={<Translate id="Plan_List.Message.No_Result.Next_Action" />}
        />
      )}
    </>
  );
}

export default PlanTabSelection;
